import React, { useCallback, useEffect, useState } from "react";
import { Search, FileDown, Sheet, Plus } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { hasPerm } from "../../lib/permissions";
import { PageHead, Field, PortPicker, usePorts, CONTAINERS, CARGOS, CURRENCIES, money, ago, RateBadge, pdfUrl, Empty } from "./common";

const BLANK = {
  rate_category: "manual", provider_name: "", origin_port_id: "", destination_port_id: "",
  container_type: "20FT", cargo_type: "General", currency: "USD",
  ocean_freight: "", baf: "", peak_season_surcharge: "", origin_charges: "",
  destination_charges: "", documentation_charges: "", other_charges: "",
  transit_days: "", departure_schedule: "", valid_until: "", data_source: "", remarks: "",
};

export default function RateSearch() {
  const { user } = useAuth();
  const ports = usePorts();
  const [f, setF] = useState({ origin_port_id: "", destination_port_id: "", container_type: "20FT", cargo_type: "", currency: "", category: "", provider: "", sort: "cost", only_valid: false, shipment_date: "", weight: "", volume: "" });
  const [res, setRes] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);

  const canCreate = hasPerm(user, "sea_freight", "create");

  const search = useCallback(async (override) => {
    const q = { ...f, ...(override || {}) };
    setLoading(true);
    try {
      const { data } = await api.get("/sea-freight/rates/search", {
        params: {
          origin_port_id: q.origin_port_id, destination_port_id: q.destination_port_id,
          container_type: q.container_type, cargo_type: q.cargo_type, currency: q.currency,
          provider: q.provider, category: q.category, only_valid: q.only_valid, sort: q.sort,
        },
      });
      setRes(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  }, [f]);

  useEffect(() => { api.get("/sea-freight/rate-meta").then((r) => setMeta(r.data)).catch(() => {}); search({}); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    try {
      await api.post("/sea-freight/rates", { ...form });
      toast.success("Rate observation saved");
      setOpen(false); setForm(BLANK); search({});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const total = (r) => r.ocean_freight + r.baf + r.peak_season_surcharge + r.origin_charges + r.destination_charges + r.documentation_charges + r.other_charges;

  return (
    <div data-testid="rate-search-page">
      <PageHead title="Freight Rate Search"
        subtitle="Search and compare container rates across providers. Every result shows its rate category, source and timestamp — only API Verified and Forwarder Quoted rates are bookable."
        right={<>
          <Button data-testid="export-rates-pdf" variant="outline" className="rounded-sm gap-1.5"
            onClick={() => window.open(pdfUrl("/sea-freight/export/rates.pdf", { origin_port_id: f.origin_port_id, destination_port_id: f.destination_port_id, container_type: f.container_type, currency: f.currency }), "_blank")}>
            <FileDown className="w-4 h-4" /> PDF
          </Button>
          <Button data-testid="export-rates-excel" variant="outline" className="rounded-sm gap-1.5"
            onClick={() => window.open(pdfUrl("/sea-freight/export/rates.xlsx", { origin_port_id: f.origin_port_id, destination_port_id: f.destination_port_id, container_type: f.container_type, currency: f.currency }), "_blank")}>
            <Sheet className="w-4 h-4" /> Excel
          </Button>
          {canCreate && <Button data-testid="add-rate-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5" onClick={() => setOpen(true)}><Plus className="w-4 h-4" /> Add rate</Button>}
        </>} />

      <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Field label="Origin port"><PortPicker testid="origin-port" ports={ports} value={f.origin_port_id} onChange={(v) => setF({ ...f, origin_port_id: v })} /></Field>
          <Field label="Destination port"><PortPicker testid="destination-port" ports={ports} value={f.destination_port_id} onChange={(v) => setF({ ...f, destination_port_id: v })} /></Field>
          <Field label="Container type">
            <Select value={f.container_type || "all"} onValueChange={(v) => setF({ ...f, container_type: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-container" className="bg-white h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All containers</SelectItem>{CONTAINERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Cargo type">
            <Select value={f.cargo_type || "all"} onValueChange={(v) => setF({ ...f, cargo_type: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-cargo" className="bg-white h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All cargo</SelectItem>{CARGOS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Shipment date"><Input data-testid="filter-date" type="date" value={f.shipment_date} onChange={(e) => setF({ ...f, shipment_date: e.target.value })} className="bg-white h-9" /></Field>
          <Field label="Currency">
            <Select value={f.currency || "all"} onValueChange={(v) => setF({ ...f, currency: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-currency" className="bg-white h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All currencies</SelectItem>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Weight (MT, optional)"><Input data-testid="filter-weight" type="number" min="0" value={f.weight} onChange={(e) => setF({ ...f, weight: e.target.value })} className="bg-white h-9" /></Field>
          <Field label="Volume (CBM, optional)"><Input data-testid="filter-volume" type="number" min="0" value={f.volume} onChange={(e) => setF({ ...f, volume: e.target.value })} className="bg-white h-9" /></Field>
          <Field label="Rate source / category">
            <Select value={f.category || "all"} onValueChange={(v) => setF({ ...f, category: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-category" className="bg-white h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All rate categories</SelectItem>
                {Object.entries(meta?.categories || {}).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Shipping line / provider">
            <Select value={f.provider || "all"} onValueChange={(v) => setF({ ...f, provider: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="filter-provider" className="bg-white h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All providers</SelectItem>{(res?.providers || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Sort by">
            <Select value={f.sort} onValueChange={(v) => setF({ ...f, sort: v })}>
              <SelectTrigger data-testid="filter-sort" className="bg-white h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cost">Lowest total cost</SelectItem>
                <SelectItem value="transit">Fastest transit time</SelectItem>
                <SelectItem value="recent">Most recently observed</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Rate validity">
            <Select value={f.only_valid ? "valid" : "all"} onValueChange={(v) => setF({ ...f, only_valid: v === "valid" })}>
              <SelectTrigger data-testid="filter-validity" className="bg-white h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Include expired rates</SelectItem><SelectItem value="valid">Only currently valid</SelectItem></SelectContent>
            </Select>
          </Field>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <Button data-testid="search-rates-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5" onClick={() => search({})} disabled={loading}>
            <Search className="w-4 h-4" /> {loading ? "Searching..." : "Search rates"}
          </Button>
          {meta && !meta.live_api_connected && (
            <span className="text-xs text-amber-700" data-testid="no-live-api-note">No live rate API connected — results come from quotations and recorded observations.</span>
          )}
        </div>
      </div>

      {res?.message && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-sm px-4 py-3 text-sm mb-4" data-testid="no-rate-message">{res.message}</div>
      )}

      {res && res.results.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="rate-results">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Provider", "Lane", "Ctr / Cargo", "Ocean", "BAF", "PSS", "Origin", "Destination", "Docs", "Other", "Total", "Transit", "Schedule", "Validity", "Rate category", "Source / updated"].map((h) => (
                  <th key={h} className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {res.results.map((r) => (
                <tr key={r.id} data-testid={`rate-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-800">{r.provider_name}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{r.origin_port} → {r.destination_port}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{r.container_type} / {r.cargo_type}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{money(r.ocean_freight, r.currency)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{money(r.baf, r.currency)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{money(r.peak_season_surcharge, r.currency)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{money(r.origin_charges, r.currency)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{money(r.destination_charges, r.currency)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{money(r.documentation_charges, r.currency)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums">{money(r.other_charges, r.currency)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums font-semibold text-slate-900">{money(total(r), r.currency)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{r.transit_days ? `${r.transit_days} days` : "-"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">{r.departure_schedule || "-"}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-slate-600">
                    {r.valid_until || "-"} {r.expired && <Badge variant="outline" className="ml-1 rounded-full bg-red-100 text-red-700 border-red-200 text-[10px] px-1.5">expired</Badge>}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap"><RateBadge row={r} /></td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-[11px] text-slate-500">{r.data_source}<span className="block text-slate-400">{ago(r.observed_at)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {res && res.results.length === 0 && !res.message && <Empty testid="rates-empty">No rate matched your filters.</Empty>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="add-rate-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Add freight rate observation</DialogTitle>
            <DialogDescription>Record a rate you received or read. Pick the correct category — indicative rates are never shown as bookable.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Rate category">
              <Select value={form.rate_category} onValueChange={(v) => setForm({ ...form, rate_category: v })}>
                <SelectTrigger data-testid="rate-category" className="bg-white h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(meta?.categories || {}).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Provider / shipping line"><Input data-testid="rate-provider" value={form.provider_name} onChange={(e) => setForm({ ...form, provider_name: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="Currency">
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="rate-currency" className="bg-white h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Origin port"><PortPicker testid="rate-origin" ports={ports} value={form.origin_port_id} onChange={(v) => setForm({ ...form, origin_port_id: v })} /></Field>
            <Field label="Destination port"><PortPicker testid="rate-destination" ports={ports} value={form.destination_port_id} onChange={(v) => setForm({ ...form, destination_port_id: v })} /></Field>
            <Field label="Container type">
              <Select value={form.container_type} onValueChange={(v) => setForm({ ...form, container_type: v })}>
                <SelectTrigger data-testid="rate-container" className="bg-white h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{CONTAINERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {[["ocean_freight", "Ocean freight"], ["baf", "BAF / fuel surcharge"], ["peak_season_surcharge", "Peak season surcharge"],
              ["origin_charges", "Origin charges"], ["destination_charges", "Destination charges"],
              ["documentation_charges", "Documentation charges"], ["other_charges", "Other charges"],
              ["transit_days", "Transit time (days)"]].map(([k, label]) => (
              <Field key={k} label={label}>
                <Input data-testid={`rate-${k}`} type="number" min="0" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="bg-white h-9" />
              </Field>
            ))}
            <Field label="Cargo type">
              <Select value={form.cargo_type} onValueChange={(v) => setForm({ ...form, cargo_type: v })}>
                <SelectTrigger data-testid="rate-cargo" className="bg-white h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{CARGOS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Departure schedule"><Input data-testid="rate-schedule" value={form.departure_schedule} onChange={(e) => setForm({ ...form, departure_schedule: e.target.value })} placeholder="e.g. Weekly, Tuesday" className="bg-white h-9" /></Field>
            <Field label="Rate valid until"><Input data-testid="rate-valid-until" type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="Data source"><Input data-testid="rate-source" value={form.data_source} onChange={(e) => setForm({ ...form, data_source: e.target.value })} placeholder="e.g. Agent email 12-Jun" className="bg-white h-9" /></Field>
            <div className="sm:col-span-3"><Field label="Remarks"><Input data-testid="rate-remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="bg-white h-9" /></Field></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-rate-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm" onClick={save}>Save rate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
